import { t } from "@lingui/macro";
import { Button, Input, Label } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { CheckCircle2, Plus, X } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";

import { useAudioChatStore } from "@/client/stores/audioChatStore";

import { recordLegacyEvent, recordLegacyStateUpdate } from "./_utils/legacyTranscriptBridge";

type ExtrasField = "awards" | "certifications" | "languages" | "hobbies";
type ExtrasChatProps = {
  onDone: () => void;
};
type ExtrasInputState = Record<ExtrasField, string>;
type ExtrasErrorState = Partial<Record<ExtrasField, string>>;

const extrasPromptId = "extras-list-editor";

const emptyInputs: ExtrasInputState = {
  awards: "",
  certifications: "",
  languages: "",
  hobbies: "",
};

const hasValue = (values: string[], value: string) =>
  values.some((currentValue) => currentValue.toLowerCase() === value.toLowerCase());

export const ExtrasChat = ({ onDone }: ExtrasChatProps) => {
  const [inputs, setInputs] = useState<ExtrasInputState>(emptyInputs);
  const [errors, setErrors] = useState<ExtrasErrorState>({});
  const [activeField, setActiveField] = useState<ExtrasField>("awards");
  const chatTextData = useAudioChatStore((state) => state.chatTextData);
  const setChatTextData = useAudioChatStore((state) => state.setChatTextData);
  const awardsInfo = useAudioChatStore((state) => state.awardsInfo);
  const certificationInfo = useAudioChatStore((state) => state.certificationInfo);
  const languagesInfo = useAudioChatStore((state) => state.languagesInfo);
  const hobbiesInfo = useAudioChatStore((state) => state.hobbiesInfo);
  const updateAwardsInfo = useAudioChatStore((state) => state.updateAwardsInfo);
  const updateCertificationInfo = useAudioChatStore((state) => state.updateCertificationInfo);
  const updateLanguagesInfo = useAudioChatStore((state) => state.updateLanguagesInfo);
  const updateHobbiesInfo = useAudioChatStore((state) => state.updateHobbiesInfo);

  const sections: Array<{
    field: ExtrasField;
    title: string;
    helper: string;
    placeholder: string;
    values: string[];
    updateValue: (value: string) => void;
    path: string;
  }> = [
    {
      field: "awards",
      title: t`Awards`,
      helper: t`Recognition, scholarships, publications, or notable wins.`,
      placeholder: t`Dean's List, Hackathon winner`,
      values: awardsInfo,
      updateValue: updateAwardsInfo,
      path: "extras.awards",
    },
    {
      field: "certifications",
      title: t`Certifications`,
      helper: t`Licences, certificates, or completed professional training.`,
      placeholder: t`AWS Certified Developer`,
      values: certificationInfo,
      updateValue: updateCertificationInfo,
      path: "extras.certifications",
    },
    {
      field: "languages",
      title: t`Languages`,
      helper: t`Languages you can use professionally or conversationally.`,
      placeholder: t`English, Hindi, Spanish`,
      values: languagesInfo,
      updateValue: updateLanguagesInfo,
      path: "extras.languages",
    },
    {
      field: "hobbies",
      title: t`Hobbies`,
      helper: t`Interests that add useful personality or context.`,
      placeholder: t`Open source, public speaking`,
      values: hobbiesInfo,
      updateValue: updateHobbiesInfo,
      path: "extras.interests",
    },
  ];

  const updateInput = (field: ExtrasField, value: string) => {
    setInputs((currentInputs) => ({ ...currentInputs, [field]: value }));
    setErrors((currentErrors) => ({ ...currentErrors, [field]: undefined }));
  };

  const addItem = (field: ExtrasField, rawValue: string) => {
    const section = sections.find((currentSection) => currentSection.field === field);
    const value = rawValue.trim();
    if (!section) return;

    if (!value) {
      setErrors((currentErrors) => ({ ...currentErrors, [field]: t`Enter a value first.` }));
      return;
    }

    if (hasValue(section.values, value)) {
      setErrors((currentErrors) => ({
        ...currentErrors,
        [field]: t`That item is already listed.`,
      }));
      return;
    }

    section.updateValue(value);
    recordLegacyStateUpdate(section.path, [...section.values, value]);
    setInputs((currentInputs) => ({ ...currentInputs, [field]: "" }));
    setErrors((currentErrors) => ({ ...currentErrors, [field]: undefined }));
  };

  const removeItem = (field: ExtrasField, value: string) => {
    const section = sections.find((currentSection) => currentSection.field === field);
    if (!section) return;

    section.updateValue(value);
    recordLegacyStateUpdate(
      section.path,
      section.values.filter((currentValue) => currentValue !== value),
    );
  };

  const submitSection = (event: FormEvent<HTMLFormElement>, field: ExtrasField) => {
    event.preventDefault();
    addItem(field, inputs[field]);
  };

  const handleDone = () => {
    recordLegacyEvent({
      type: "prompt_submitted",
      promptId: extrasPromptId,
      value: {
        awards: awardsInfo,
        certifications: certificationInfo,
        languages: languagesInfo,
        hobbies: hobbiesInfo,
      },
    });
    onDone();
  };

  useEffect(() => {
    recordLegacyEvent({ type: "prompt_shown", promptId: extrasPromptId });
  }, []);

  useEffect(() => {
    if (chatTextData) {
      addItem(activeField, chatTextData);
      setChatTextData("");
    }
  }, [chatTextData]);

  return (
    <div className="mx-1 mb-5 w-[min(100%,52rem)] rounded-lg border border-border bg-background p-4 shadow-sm">
      <div className="mb-4 flex flex-col gap-1">
        <h3 className="text-sm font-semibold text-foreground">{t`Optional details`}</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t`Add only what strengthens the resume. Empty sections will be ignored.`}
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {sections.map(({ field, title, helper, placeholder, values }) => {
          const error = errors[field];
          const inputId = `extras-${field}`;
          const errorId = `extras-${field}-error`;

          return (
            <section
              key={field}
              className="min-w-0 rounded-md border border-border bg-secondary/20 p-3"
            >
              <div className="mb-3">
                <h4 className="text-sm font-semibold text-foreground">{title}</h4>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{helper}</p>
              </div>

              <form onSubmit={(event) => submitSection(event, field)} className="space-y-1.5">
                <Label htmlFor={inputId} className="sr-only">
                  {title}
                </Label>
                <div className="flex gap-2">
                  <Input
                    id={inputId}
                    value={inputs[field]}
                    placeholder={placeholder}
                    aria-invalid={Boolean(error)}
                    aria-describedby={error ? errorId : undefined}
                    onFocus={() => setActiveField(field)}
                    onChange={(event) => updateInput(field, event.target.value)}
                    className={cn(
                      "!h-10 !rounded-md !border bg-background text-sm shadow-none focus-visible:ring-2 focus-visible:ring-primary/30",
                      error && "!border-error focus-visible:ring-error/30",
                    )}
                  />
                  <Button type="submit" variant="outline" size="icon" aria-label={t`Add ${title}`}>
                    <Plus size={16} aria-hidden="true" />
                  </Button>
                </div>
                {error && (
                  <p id={errorId} className="text-xs font-medium text-error">
                    {error}
                  </p>
                )}
              </form>

              {values.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {values.map((value) => (
                    <span
                      key={value}
                      className="inline-flex max-w-full items-center gap-1 rounded-full bg-background px-2.5 py-1 text-sm text-foreground shadow-sm"
                    >
                      <span className="truncate">{value}</span>
                      <button
                        type="button"
                        aria-label={t`Remove ${value}`}
                        onClick={() => removeItem(field, value)}
                        className="rounded-full p-0.5 text-gray-500 transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
                      >
                        <X size={14} aria-hidden="true" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </section>
          );
        })}
      </div>

      <div className="mt-4 flex justify-end">
        <Button type="button" size="md" className="gap-2" onClick={handleDone}>
          <CheckCircle2 size={16} aria-hidden="true" />
          {t`Done`}
        </Button>
      </div>
    </div>
  );
};
